// src/components/Toolbar/FileActions/OpenButton.tsx
import React, { useRef, useState } from 'react';
import { FolderOpen, Check, AlertCircle } from 'lucide-react';
import { importFromJSON } from '@features/export';
import { useInitialDataManager } from '@hooks/useInitialDataManager';
import { useUnsavedChanges } from '@hooks/useUnsavedChanges';
import { toast } from '@/stores/toastStore';

export const OpenButton: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isOpening, setIsOpening] = useState(false);

  // Use the proper initialization manager
  const { load } = useInitialDataManager();

  // Track unsaved changes
  const { hasUnsavedChanges, reset: resetUnsavedChanges } = useUnsavedChanges();

  const handleOpen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast.warning('Please select a JSON file.');
      return;
    }

    // Warn about unsaved changes
    if (hasUnsavedChanges) {
      const confirmOpen = window.confirm(
        'You have unsaved changes. Opening a new diagram will discard them. Continue?'
      );
      if (!confirmOpen) {
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    setIsOpening(true);

    try {
      // Import the JSON data
      const importedModel = await importFromJSON(file);

      // Use the proper load function
      // This will log validation errors if any
      load(importedModel);

      // Reset unsaved changes tracking since we just loaded a new diagram
      resetUnsavedChanges();

      // Note: resetUiState() removed - it conflicts with load() state setup
      // and causes screen tearing by triggering additional re-renders

      setStatus('success');
      toast.success('Diagram opened successfully.');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (error) {
      console.error('❌ Open failed:', error);
      setStatus('error');

      // Show more detailed error
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      toast.error(
        `Failed to open diagram: ${errorMessage}\n\nCheck browser console (F12) for detailed validation errors.`
      );

      setTimeout(() => setStatus('idle'), 3000);
    } finally {
      setIsOpening(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getButtonContent = () => {
    if (isOpening) {
      return (
        <>
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent dark:border-white dark:border-t-transparent" />
          <span>Opening...</span>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Check className="h-4 w-4" />
          <span>Opened!</span>
        </>
      );
    }

    if (status === 'error') {
      return (
        <>
          <AlertCircle className="h-4 w-4" />
          <span>Failed</span>
        </>
      );
    }

    return (
      <>
        <FolderOpen className="h-4 w-4" />
        <span>Open</span>
      </>
    );
  };

  // const getButtonColor = () => {
  //   if (status === 'success') return 'text-green-600 dark:text-green-400';
  //   if (status === 'error') return 'text-red-600 dark:text-red-400';
  //   return 'text-gray-700 dark:text-gray-300';
  // };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleOpen}
        className="hidden"
      />
      <button
        data-open-button
        onClick={() => fileInputRef.current?.click()}
        disabled={isOpening}
        className="group relative flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 transition-all hover:bg-white hover:text-gray-900 hover:shadow-sm disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
        title="Open diagram from JSON file (Ctrl+O)"
      >
        {getButtonContent()}
      </button>
    </>
  );
};
